// DS — jedyny prymityw dialogu. Każdy modal w aplikacji stoi na nim.
//
// JEDNA ANATOMIA (reguła 49 języka minimal, MAPA 30-R6, klatki R6-00…R6-10):
// na telefonie KAŻDE okno, menu, lista wyboru i panel jest arkuszem od dołu:
//
//   ┌ nagłówek 56 px, przyklejony: tytuł 16/600 w jednej linii ┄┄ X 44 × 44 ┐
//   │ treść — JEDYNY przewijany element arkusza                            │
//   └ stopka (tylko gdy jest akcja): przyklejona, kreska u góry, 48 px ─────┘
//
// Zachowanie:
//   · fokus wchodzi na TYTUŁ (czytnik czyta nazwę arkusza), chyba że arkusz wskaże
//     bezpieczną akcję albo pole przez [data-autofocus] — tam ląduje wtedy
//   · Tab / Shift+Tab krążą wewnątrz; Escape zamyka (chyba że brak `onClose`)
//   · klik w zasłonę zamyka; „wstecz” zamyka TO okno, nie ekran pod nim
//   · X to JEDYNY widoczny sposób zamknięcia. Ukryte zostają wszystkie: zasłona,
//     Escape, „wstecz” i pociągnięcie NAGŁÓWKA w dół. Uchwytu nie rysujemy — obiecywał
//     gest, który na przewijanej treści kłóci się z przewijaniem (decyzja 1 MAPY 30-R6)
//   · po przewinięciu treści nagłówek dostaje kreskę 1 px (bez cienia)
//   · pułap wysokości = ekran − 56 px; przy otwartej klawiaturze liczony
//     z `visualViewport`, żeby stopka stała NAD klawiaturą, a X został widoczny
//   · arkusz w arkuszu: drugi nad pierwszym z własną zasłoną; X / Esc / „wstecz”
//     zamykają tylko górny (licznik blokady przewijania niżej)
//   · scroll strony zablokowany; role=dialog + aria-modal + aria-labelledby
//   · wejście: scrim + arkusz razem; wyjście: razem, dopiero potem odmontowanie
//     (G8-20). Klasa `.is-leaving`, czas z ds/motion.ts.

import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import Icon from '../media/Icon'
import { useNarrow } from '../../hooks/useNarrow'
import { useT } from '../../lib/i18n'
import { sheetLeaveMs } from '../motion'
import { useBackCloses } from './useBackCloses'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Próg, poniżej którego okno JEST arkuszem. To DOSŁOWNY tekst zapytania z `ds.css`,
 * `motion.css` i `minimal.css` — razem z gałęzią telefonu POŁOŻONEGO (844 × 390,
 * `R6-09`): licząc samą szerokość, JS dawał parę „Anuluj” + akcja w arkuszu, który CSS
 * już narysował (przegląd G, S1-03). Strażnik: `sheetQuery.test.tsx`.
 */
export const PHONE_SHEET_QUERY = '(max-width: 640px), (max-height: 500px) and (max-width: 900px)'

/**
 * Czy okno jest TERAZ arkuszem telefonu. Woła je arkusz, który musi ZDJĄĆ z dołu cichą
 * drogę wyjścia (reguła 49) — schowanie jej CSS-em zostawiłoby ją czytnikowi ekranu
 * i bramce, a arkusz bez akcji ma mieć na dole nic, a nie „nic widać”.
 */
export function usePhoneSheet(): boolean {
  return useNarrow(PHONE_SHEET_QUERY)
}

function isTopmostDialog(shell: HTMLElement): boolean {
  const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')
  return dialogs.length > 0 && dialogs[dialogs.length - 1] === shell
}

// Blokada scrolla tła jest LICZONA, nie „każdy dialog pamięta poprzednią wartość”: dwa dialogi
// naraz (arkusz listy nad oknem kroku) odmontowują się w jednym commicie rodzic → dziecko,
// więc rodzic oddałby stronie jej scroll, a dziecko zaraz potem oddałoby „hidden” rodzica —
// i strona zostałaby zablokowana na stałe (recenzja 2026-09-17: wstecz w przeglądarce
// z otwartym arkuszem). Pierwszy zamek zapamiętuje wartość sprzed, ostatni ją oddaje.
//
// Szuflada nawigacji 390 (`components/shell/NavDrawer`) liczy się do TEGO SAMEGO
// licznika — jest warstwą nad stroną jak każde okno, a okno otwarte nad szufladą nie
// ma prawa oddać strony scrollowi, kiedy samo się zamyka. Stąd eksport.
let scrollLocks = 0
let scrollBefore = ''
export function lockPageScroll() {
  if (scrollLocks++ === 0) {
    scrollBefore = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
}
export function unlockPageScroll() {
  if (--scrollLocks === 0) document.body.style.overflow = scrollBefore
}

export type DialogProps = {
  children: ReactNode
  /** Brak = dialog nie do odrzucenia (bez zasłony-klik, Escape i X). */
  onClose?: () => void
  /**
   * Tytuł w przyklejonym nagłówku: jedna linia z wielokropkiem, obok X.
   * Bierze też `aria-labelledby`, więc arkusz z tytułem nie potrzebuje `label`.
   */
  title?: ReactNode
  /**
   * Przyklejona stopka. Arkusz BEZ AKCJI jej nie ma — „Zamknij” i „Anuluj” zniknęły
   * z dołu (reguła 49); rezygnacja to X. Arkusz z akcją podaje tu JEDNĄ akcję główną,
   * kasujący i okno wyjścia — parę w jednym wierszu.
   */
  footer?: ReactNode
  /** Dodatkowa klasa stopki — `dg-foot--pair` dla pary w jednym wierszu. */
  footerClassName?: string
  /** Szersza karta (listy, formularze). */
  wide?: boolean
  /**
   * Nazwa dostępna, gdy arkusz nie ma tytułu w nagłówku (podgląd, lightbox).
   * Widoczny nagłówek spoza `title` podpina się przez `labelledBy`.
   */
  label?: string
  labelledBy?: string
  /** Etykieta X dla czytnika. Domyślnie z locale (`dialogs.close`). */
  closeLabel?: string
  className?: string
  /** Uchwyt testowy na CAŁYM arkuszu — razem z nagłówkiem i stopką. */
  'data-testid'?: string
}

// Pociągnięcie nagłówka zamyka: dość daleko albo krótkie szarpnięcie.
const SHEET_CLOSE_PX = 96
const SHEET_FLING_MS = 300
const SHEET_FLING_PX = 24

export function Dialog({
  children, onClose, title, footer, footerClassName, wide, label, labelledBy, closeLabel, className = '', ...rest
}: DialogProps) {
  const shellRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)
  const autoId = useId()
  const titleId = `dg-${autoId}`
  const t = useT()
  const [leaving, setLeaving] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Zamknięcie = najpierw wyjście (klasa), potem `onClose` po czasie z tokenu.
  const close = useCallback(() => {
    if (!onClose || timer.current) return
    const ms = sheetLeaveMs()
    if (ms <= 0) { onClose(); return }
    setLeaving(true)
    timer.current = setTimeout(() => { timer.current = null; onClose() }, ms)
  }, [onClose])

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  // Zapamiętaj fokus, wejdź do środka, oddaj przy odmontowaniu. Kolejność wyboru celu
  // jest wyborem PROJEKTOWYM (R6, dostępność): arkusz kasujący i pole tekstowe mówią
  // o sobie przez [data-autofocus], każdy inny arkusz wpuszcza fokus na TYTUŁ — czytnik
  // czyta nazwę arkusza, a nie pierwszą pozycję listy.
  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null
    const shell = shellRef.current
    if (shell) {
      const preferred = shell.querySelector<HTMLElement>('[data-autofocus]')
      const fallback = shell.querySelector<HTMLElement>(`${FOCUSABLE}:not(.dg-close)`)
      ;(preferred ?? titleRef.current ?? fallback ?? shell).focus({ preventScroll: true })
    }
    return () => {
      const active = document.activeElement
      if (!active || active === document.body || shell?.contains(active)) {
        restoreTo.current?.focus?.({ preventScroll: true })
      }
    }
  }, [])

  // Blokada scrolla tła; ostatni zamknięty dialog przywraca dokładnie wartość sprzed pierwszego.
  useEffect(() => {
    lockPageScroll()
    return unlockPageScroll
  }, [])

  // Klawiatura telefonu zjada dolną część ekranu, a `100dvh` jej nie widzi: pułap
  // arkusza liczymy z `visualViewport`, więc stopka z akcją stoi NAD klawiaturą,
  // a nagłówek z X zostaje widoczny (R6-06).
  useEffect(() => {
    const vv = typeof window === 'undefined' ? null : window.visualViewport
    if (!vv) return
    const apply = () => {
      shellRef.current?.style.setProperty('--dg-vvh', `${Math.round(vv.height)}px`)
    }
    apply()
    vv.addEventListener('resize', apply)
    vv.addEventListener('scroll', apply)
    return () => {
      vv.removeEventListener('resize', apply)
      vv.removeEventListener('scroll', apply)
    }
  }, [])

  // Escape + pułapka fokusa.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const shell = shellRef.current
      if (!shell || !isTopmostDialog(shell)) return
      if (e.key === 'Escape' && onClose) {
        e.stopPropagation()
        close()
        return
      }
      if (e.key !== 'Tab') return
      const items = Array.from(shell.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement)
      if (items.length === 0) {
        e.preventDefault()
        shell.focus({ preventScroll: true })
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey && (active === first || active === shell || active === titleRef.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [close, onClose])

  // „Wstecz” zamyka TO okno, nie ekran pod nim. Okno bez `onClose` (postęp renderu,
  // którego nie da się odrzucić) nie zabiera wpisu z historii — wstecz je pomija.
  useBackCloses(Boolean(onClose), close)

  // Pociągnięcie zamyka arkusz WYŁĄCZNIE za nagłówek (sposób UKRYTY, bez rysunku):
  // treść przewija się normalnie, a nagłówek ma `touch-action: none` i przechwycenie
  // wskaźnika, więc gest nigdy nie rusza strony pod spodem ani nie gubi palca.
  const drag = useRef<{ id: number; y0: number; t0: number } | null>(null)

  const dragTo = (dy: number) => {
    const shell = shellRef.current
    if (shell) shell.style.transform = dy > 0 ? `translateY(${dy}px)` : ''
  }

  const onHeadDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // X jest przyciskiem, nie uchwytem: gest zaczęty na nim nie ciągnie arkusza.
    if (!onClose || drag.current || (e.target as HTMLElement).closest('button')) return
    drag.current = { id: e.pointerId, y0: e.clientY, t0: Date.now() }
    const el = e.currentTarget
    // jsdom (i stare WebView) nie mają przechwytywania wskaźnika — gest działa
    // dalej na zdarzeniach, które i tak bąbelkują.
    if (typeof el.setPointerCapture === 'function') {
      try { el.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    }
  }

  const onHeadMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    dragTo(e.clientY - d.y0)
  }

  const onHeadUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    drag.current = null
    dragTo(0)
    const dy = e.clientY - d.y0
    const flick = Date.now() - d.t0 < SHEET_FLING_MS && dy > SHEET_FLING_PX
    if (dy > SHEET_CLOSE_PX || flick) close()
  }

  // Przerwany gest (system przejął wskaźnik) wraca na miejsce i NIE zamyka.
  const onHeadCancel = () => {
    drag.current = null
    dragTo(0)
  }

  const hasHead = Boolean(title || onClose)
  const describedByTitle = title ? titleId : labelledBy

  return (
    <div
      className={`overlay${leaving ? ' is-leaving' : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}
    >
      <div
        ref={shellRef}
        className={['dialog', wide && 'wide', className].filter(Boolean).join(' ')}
        role="dialog"
        data-testid={rest['data-testid']}
        aria-modal="true"
        aria-label={describedByTitle ? undefined : label}
        aria-labelledby={describedByTitle}
        tabIndex={-1}
      >
        {hasHead ? (
          <div
            className={`dg-head${scrolled ? ' is-scrolled' : ''}`}
            style={{ touchAction: 'none' }}
            onPointerDown={onHeadDown}
            onPointerMove={onHeadMove}
            onPointerUp={onHeadUp}
            onPointerCancel={onHeadCancel}
          >
            {/* Tytuł bierze fokus programowo (tabIndex -1), więc czytnik czyta nazwę
                arkusza, a klawiatura zaczyna Tabem od X. */}
            {title ? <h2 className="dg-title" id={titleId} ref={titleRef} tabIndex={-1}>{title}</h2> : <span className="dg-title" />}
            {onClose ? (
              <button type="button" className="dg-close" onClick={close} aria-label={closeLabel ?? t('dialogs.close')}>
                <Icon name="X" size={20} />
              </button>
            ) : null}
          </div>
        ) : null}
        {/* Treść to JEDYNY przewijany element arkusza; kreska pod nagłówkiem zapala się
            dopiero, gdy naprawdę jest co przewijać nad krawędzią. */}
        <div
          ref={bodyRef}
          className="dg-body"
          onScroll={(e) => {
            const next = e.currentTarget.scrollTop > 0
            setScrolled((prev) => (prev === next ? prev : next))
          }}
        >
          {children}
        </div>
        {footer ? <div className={['dg-foot', footerClassName].filter(Boolean).join(' ')}>{footer}</div> : null}
      </div>
    </div>
  )
}

/**
 * Back-compat alias. The app-kit port called this `Overlay`; new code should
 * import `Dialog`.
 */
export const Overlay = Dialog

export default Dialog
